import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  OnboardingStatus,
  getOnboardingStatus,
  markWelcomeSeen,
  startTour,
  completeTourStep,
  completeTour,
  skipTour,
  registerMilestone,
  dismissTip,
  dismissSpotlight,
  updatePreferences,
  resetOnboarding
} from '../services/onboardingService';

interface OnboardingContextType {
  // State
  status: OnboardingStatus | null;
  loading: boolean;
  error: string | null;
  
  // Computed
  shouldShowWelcome: boolean;
  shouldShowTour: boolean;
  progressPercent: number;
  
  // Actions
  refreshStatus: () => Promise<void>;
  handleWelcomeSeen: () => Promise<void>;
  handleStartTour: () => Promise<void>;
  handleTourStepComplete: (stepId: string, stepIndex: number) => Promise<void>;
  handleTourComplete: () => Promise<void>;
  handleSkipTour: () => Promise<void>;
  handleMilestone: (milestone: 'firstSearch' | 'firstDownload' | 'firstFavorite' | 'firstCollection' | 'firstUpload' | 'usedAdvancedSearch' | 'usedBulkOperations' | 'viewedBrandGuide') => Promise<void>;
  handleDismissTip: (tipId: string) => Promise<void>;
  handleDismissSpotlight: (spotlightId: string) => Promise<void>;
  handleUpdatePreferences: (preferences: Partial<OnboardingStatus['preferences']>) => Promise<void>;
  handleResetOnboarding: () => Promise<void>;
  
  // Helpers
  isTipDismissed: (tipId: string) => boolean;
  isSpotlightDismissed: (spotlightId: string) => boolean;
  isMilestoneCompleted: (milestone: keyof OnboardingStatus['milestones']) => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function OnboardingProvider({ children, enabled = true }: OnboardingProviderProps) {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ladda status vid mount
  const refreshStatus = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await getOnboardingStatus();
      setStatus(data);
    } catch (err: any) {
      console.error('[Onboarding] Failed to fetch status:', err);
      setError(err.message || 'Kunde inte ladda onboarding-status');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Computed values
  const shouldShowWelcome = !loading && status !== null && !status.hasSeenWelcome;
  const shouldShowTour = !loading && status !== null && status.hasSeenWelcome && !status.hasCompletedTour;

  // Beräkna progress (antal milstolpar klarade / totalt)
  const progressPercent = status ? (() => {
    const milestones = status.milestones;
    const progressKeys: (keyof OnboardingStatus['milestones'])[] = [
      'firstSearch',
      'firstDownload',
      'firstCollection',
      'firstUpload',
      'usedAdvancedSearch',
      'usedBulkOperations',
      'viewedBrandGuide'
    ];
    const completed = progressKeys.filter(key => milestones[key]).length;
    const total = progressKeys.length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  })() : 0;

  // Actions
  const handleWelcomeSeen = useCallback(async () => {
    try {
      const data = await markWelcomeSeen();
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to mark welcome seen:', err);
    }
  }, []);

  const handleStartTour = useCallback(async () => {
    try {
      const data = await startTour();
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to start tour:', err);
    }
  }, []);

  const handleTourStepComplete = useCallback(async (stepId: string, stepIndex: number) => {
    try {
      const data = await completeTourStep(stepId, stepIndex);
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to complete step:', err);
    }
  }, []);

  const handleTourComplete = useCallback(async () => {
    try {
      const data = await completeTour();
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to complete tour:', err);
    }
  }, []);

  const handleSkipTour = useCallback(async () => {
    try {
      const data = await skipTour();
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to skip tour:', err);
    }
  }, []);

  const handleMilestone = useCallback(async (
    milestone: 'firstSearch' | 'firstDownload' | 'firstFavorite' | 'firstCollection' | 
               'firstUpload' | 'usedAdvancedSearch' | 'usedBulkOperations' | 'viewedBrandGuide'
  ) => {
    // Kolla om redan registrerad
    if (status?.milestones[milestone]) return;
    
    try {
      const data = await registerMilestone(milestone);
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to register milestone:', err);
    }
  }, [status]);

  const handleDismissTip = useCallback(async (tipId: string) => {
    try {
      const data = await dismissTip(tipId);
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to dismiss tip:', err);
    }
  }, []);

  const handleDismissSpotlight = useCallback(async (spotlightId: string) => {
    try {
      const data = await dismissSpotlight(spotlightId);
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to dismiss spotlight:', err);
    }
  }, []);

  const handleUpdatePreferences = useCallback(async (
    preferences: Partial<OnboardingStatus['preferences']>
  ) => {
    try {
      const data = await updatePreferences(preferences);
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to update preferences:', err);
    }
  }, []);

  const handleResetOnboarding = useCallback(async () => {
    try {
      const data = await resetOnboarding();
      setStatus(data);
    } catch (err) {
      console.error('[Onboarding] Failed to reset onboarding:', err);
    }
  }, []);

  // Helpers
  const isTipDismissed = useCallback((tipId: string) => {
    return status?.dismissedTips.includes(tipId) ?? false;
  }, [status]);

  const isSpotlightDismissed = useCallback((spotlightId: string) => {
    return status?.dismissedSpotlights.includes(spotlightId) ?? false;
  }, [status]);

  const isMilestoneCompleted = useCallback((milestone: keyof OnboardingStatus['milestones']) => {
    return status?.milestones[milestone] ?? false;
  }, [status]);

  const value: OnboardingContextType = {
    status,
    loading,
    error,
    shouldShowWelcome,
    shouldShowTour,
    progressPercent,
    refreshStatus,
    handleWelcomeSeen,
    handleStartTour,
    handleTourStepComplete,
    handleTourComplete,
    handleSkipTour,
    handleMilestone,
    handleDismissTip,
    handleDismissSpotlight,
    handleUpdatePreferences,
    handleResetOnboarding,
    isTipDismissed,
    isSpotlightDismissed,
    isMilestoneCompleted
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

export default OnboardingContext;

